<template>
  <div class="hrDirectoriesEmptyState">
    <div class="headings-container">
      <HeadingLarge
        :data-test-id="$testId('importUsersHeading')"
      >
        Import users with HR Directories
      </HeadingLarge>

      <ParagraphBody
        class="heading"
        :data-test-id="$testId('importUsersDescription')"
        weight="semibold"
      >
        Select one of our pre-built HR Directory Integrations, or create a custom integration.
      </ParagraphBody>
    </div>

    <ul class="card-container">
      <li
        v-for="(card) in addHrDirectoryCards"
        :key="card.title"
        class="card"
      >
        <WayToAddHrDirectoryCard
          :configure="card.configure"
          :description="card.description"
          :href="card.href"
          :isExternal="card.isExternal"
          :logoUrl="card.logoUrl"
          :title="card.title"
        />
      </li>
    </ul>

    <div class="more-options__container">
      <HeadingLarge
        :data-test-id="$testId('moreOptionsHeading')"
      >
        Can't find what you're looking for?
      </HeadingLarge>

      <ParagraphBody
        bodySize="large"
        :data-test-id="$testId('moreOptionsDescription')"
      >
        Integrate with any HR directory by creating a custom application with robust integration
        capabilities for single sign-on and identity management.
      </ParagraphBody>

      <div class="custom-apps">
        <span v-if="$hasFeatureFlag($featureFlags.applicationWorkflow)">
          <Button
            class="configureButton"
            :data-test-id="$testId('ConfigureButton')"
            :hasOutline="true"
            text="Create Custom Application"
            type="secondary"
            @click="createNewApplication"
          />
        </span>
        <span v-else>
          <DropdownActions
            buttonText="Create Custom Application"
            :items="customApplicationActions"
          />
        </span>
      </div>
    </div>

    <ConfiguredAppsDetectedModal
      v-if="showConfiguredAppsDetectedModal"
      :appDisplayName="currentlySelectedAppDisplayName"
      :applications="previouslyConfiguredApps"
      @close="handleAppsDetectedModalClose"
      @continue="handleAppsDetectedModalContinue"
    />
  </div>
</template>

<script>
import {
  Button,
  DropdownActions,
  HeadingLarge,
  ParagraphBody,
  Types,
} from '@jumpcloud/ui-components';
import { mapActions, mapGetters } from 'vuex';

import {
  DirectoryAppsTemplatesNames,
  DirectoryTypeConsts,
  DirectoryTypeKeys,
} from 'client/util/Constants/DirectoryConstants';
import { ItemTypeConsts, ItemTypeRoutes } from 'client/util/Constants';
import { getAppTemplateRoute } from 'client/applications/utils/ApplicationUtils';

import ConfiguredAppsDetectedMixin from 'client/directories/components/ConfiguredAppsDetectedMixin';
import ConfiguredAppsDetectedModal from 'client/directories/components/ConfiguredAppsDetectedModal.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import WayToAddHrDirectoryCard from 'client/directories/components/WayToAddHrDirectoryCard.vue';
import directoryRouteMixin from 'client/components/mixins/directoryRouteMixin';

const { DropdownMenuItem } = Types;
const ApplicationTemplateCollection = 'ApplicationTemplateCollection';
const route = ItemTypeRoutes[ItemTypeConsts.application];

export default {
  name: 'HRDirectoriesEmptyState',

  components: {
    Button,
    ConfiguredAppsDetectedModal,
    DropdownActions,
    HeadingLarge,
    ParagraphBody,
    WayToAddHrDirectoryCard,
  },

  mixins: [
    ConfiguredAppsDetectedMixin,
    directoryRouteMixin,
  ],

  data: () => ({
    appTemplateRoutes: {},
  }),

  computed: {
    ...mapGetters(ApplicationTemplateCollection, [
      'genericOidcTemplateId',
      'genericTemplateId',
      'genericBookmarkId',
    ]),

    addHrDirectoryCards() {
      const cards = [
        {
          description: 'Enable SSO, import, and update users in JumpCloud',
          href: KbArticleUrls.integratingWithHiBob,
          isExternal: true,
          logoUrl: 'https://s3.amazonaws.com/static.jumpcloud.com/applications/logos/application-icon-bob.png',
          title: 'Bob',
          type: DirectoryTypeConsts.bob,
        },
        {
          description: 'Manage devices and sync user data.',
          href: KbArticleUrls.integratingWithGusto,
          isExternal: true,
          logoUrl: 'https://s3.amazonaws.com/static.jumpcloud.com/applications/logos/application-icon-gusto.png',
          title: 'Gusto',
          type: DirectoryTypeConsts.gusto,
        },
        {
          configure: () => (
            this.$router.push(this.appTemplateRoutes.namely)
          ),
          description: 'Enable SSO and import users to JumpCloud',
          logoUrl: 'https://s3.amazonaws.com/static.jumpcloud.com/applications/logos/application-icon-namely.png',
          title: 'Namely',
          type: DirectoryTypeConsts.namely,
        },
        {
          configure: async () => {
            /**
             * `checkForPreviouslyConfiguredAppsByName` is located in
             * `ConfiguredAppsDetectedMixin.js`, along with all other data
             * needed for the `ConfiguredAppsDetectedModal` component.
             *
             * This method triggers the `ConfiguredAppsDetectedModal` to display
             * (or redirect the user to this app's configure URL). More info in
             * the mixin file.
             */
            await this.checkForPreviouslyConfiguredAppsByName({
              appName: DirectoryTypeKeys.personio,
              configureURL: this.appTemplateRoutes.personio,
            });
          },
          description: 'Import and update users in JumpCloud',
          logoUrl: 'https://s3.amazonaws.com/static.jumpcloud.com/applications/logos/application-icon-personio.png',
          title: 'Personio',
          type: DirectoryTypeConsts.personio,
        },
        {
          configure: () => (
            this.$router.push(this.directoryTypeAsideRoute(DirectoryTypeConsts.workday))
          ),
          description: 'Enable SSO and import users to JumpCloud',
          logoUrl: 'https://s3.amazonaws.com/static.jumpcloud.com/applications/logos/application-icon-workday.png',
          title: 'Workday',
          type: DirectoryTypeConsts.workday,
        },
      ];

      if (this.$hasFeatureFlag(this.$featureFlags.bambooRealTimeImport)) {
        cards.unshift({
          description: 'Enable SSO, import, and update users in JumpCloud',
          href: KbArticleUrls.bambooHrIntegration,
          logoUrl: 'https://s3.amazonaws.com/static.jumpcloud.com/applications/logos/application-icon-bamboohr.png',
          isExternal: true,
          title: 'BambooHR',
          type: DirectoryTypeConsts.bammboHR,
        });
      }

      return cards;
    },

    customApplicationActions() {
      return [
        DropdownMenuItem({
          click: this.createNewSamlApplication,
          text: 'Custom IdM + SAML SSO',
        }),
        DropdownMenuItem({
          click: this.createNewOidcApplication,
          text: 'Custom IdM + OIDC SSO',
        }),
        DropdownMenuItem({
          click: this.createNewBookmark,
          text: 'Custom IdM + URL Bookmark',
        }),
      ];
    },
  },

  created() {
    DirectoryAppsTemplatesNames.forEach(async appName => {
      this.appTemplateRoutes[appName] = await getAppTemplateRoute(appName);
    });
  },

  methods: {
    ...mapActions(ApplicationTemplateCollection, [
      'fetchGenericBookmark',
      'fetchGenericOidcTemplate',
      'fetchGenericTemplate',
    ]),

    async createNewApplication() {
      this.$router.push(`${this.$options.route}/create`);
    },

    async createNewBookmark() {
      await this.fetchGenericBookmark();

      this.redirectToTemplateConfigureRoute(this.genericBookmarkId);
    },

    async createNewOidcApplication() {
      await this.fetchGenericOidcTemplate();

      this.redirectToTemplateConfigureRoute(this.genericOidcTemplateId);
    },

    async createNewSamlApplication() {
      await this.fetchGenericTemplate();

      this.redirectToTemplateConfigureRoute(this.genericTemplateId);
    },

    redirectToTemplateConfigureRoute(templateId) {
      this.$router.push(`${ItemTypeRoutes[ItemTypeConsts.ssoApplications]}/configure/${templateId}`);
    },
  },

  route,
};
</script>

<style scoped>
.hrDirectoriesEmptyState {
  align-items: center;
  display: flex;
  flex-direction: column;
  text-align: center;
}

.heading {
  text-align: center;
}

.headings-container {
  margin-bottom: var(--jc-spacer);
}

.card-container {
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  list-style: none;
  margin-bottom: calc(var(--jc-spacer-large) * 2);
}

.card {
  margin: var(--jc-spacer);
}

.more-options__container {
  width: 575px;
}

.custom-apps {
  margin: auto;
  width: 250px;
}
</style>
